import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Chip, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { DocumentKind, RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import { date } from '../../lib/format';
import { useInvestorScope } from '../../store/selectors';
import { statusLabel } from '../../ui/statusTones';
import { Card, EntityFilter, InvestorTitle } from '../common';

const KINDS: (DocumentKind | 'ALL')[] = [
  'ALL',
  'DISTRIBUTION_ADVICE',
  'TAX_STATEMENT',
  'CALL_NOTICE',
  'HOLDING_STATEMENT',
  'TRANSACTION_CONFIRMATION',
  'WHOLESALE_CERTIFICATE',
];

export default function InvestorDocumentsPage() {
  const scope = useInvestorScope();
  const [entity, setEntity] = useState('ALL');
  const [kind, setKind] = useState<DocumentKind | 'ALL'>('ALL');
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);
  const documents = useMemo(
    () =>
      scope.documents
        .filter(
          (doc) =>
            (entity === 'ALL' || doc.holderId === entity) && (kind === 'ALL' || doc.kind === kind),
        )
        .sort((left, right) => right.date.localeCompare(left.date)),
    [scope.documents, entity, kind],
  );
  return (
    <>
      <InvestorTitle
        title="Documents"
        subtitle={`${scope.documents.length} documents from the registry`}
      />
      <EntityFilter holders={scope.holders} value={entity} onChange={setEntity} />
      <Stack useFlexGap direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {KINDS.map((item) => (
          <Chip
            key={item}
            size="small"
            label={item === 'ALL' ? 'All types' : statusLabel(item)}
            color={kind === item ? 'secondary' : 'default'}
            variant={kind === item ? 'filled' : 'outlined'}
            onClick={() => setKind(item)}
          />
        ))}
      </Stack>
      <Card sx={{ p: 0 }}>
        {documents.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No documents match.
          </Typography>
        )}
        {documents.slice(0, 60).map((doc, index) => (
          <Stack
            key={doc.id}
            direction="row"
            spacing={1.5}
            alignItems="center"
            onClick={() => setViewing(doc)}
            sx={{
              px: 2,
              py: 1.5,
              borderTop: index ? '1px solid' : 'none',
              borderColor: 'divider',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'background.default' },
            }}
          >
            <DescriptionOutlinedIcon color="secondary" />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {doc.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {date(doc.date)}
            </Typography>
          </Stack>
        ))}
      </Card>
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
